/**
 * Cross-field derivations applied on every form edit.
 *
 * These are small, pure "if this changed, also fix up that" rules that cannot be
 * expressed as a single field's onChange handler because they depend on more than one
 * path in the matter document (or on reference data like the roster). Kept in one place
 * and unit-tested directly, rather than scattered across Field.jsx special cases.
 *
 * Below the hardcoded rules is one more step, applyConfiguredDerivations, which runs
 * admin-authored rules from settings/fieldDerivations (edited from Admin -> Field Builder
 * -> Auto-fill rules) — the same three behaviors below (set-a-default,
 * mirror-until-diverged, look-up-a-related-record), generalized so a firm admin can wire
 * up new ones without a code change. Each configured rule has a `kind` of 'setDefault'
 * (generalizes applyExistingClientSourceDefault), 'mirror' (generalizes
 * applyConflictCheckClientNameMirror), or 'lookup' (generalizes
 * applyPracticeGroupLeaderAutofill) — see applySetDefaultRule/applyMirrorRule/
 * applyLookupRule below for the exact semantics each one carries over. This is
 * client-only, like every rule in this file: a live-editing UX convenience, not a
 * server-enforced governance rule, so there is no functions/ port.
 */

import { evaluateCondition, getPath, setPath, unsetPath } from './conditions.js';
import { FORM_SECTIONS } from './formSchema.js';
import { isBlank, isUsableDefault } from './fieldRules.js';
import { buildEffectiveSections } from './fieldLayout.js';

/**
 * Conditional fields are not merely hidden: their values are removed as soon as their
 * condition is no longer true. This keeps old branch answers from reaching Firestore,
 * validation, approvals, PDFs, or a later re-opened branch. Display-only notices and
 * attachment metadata are deliberately excluded: neither owns a field value to clear, and
 * attachments are stored in the shared matter.attachments collection by category.
 */
function conditionalFields(config) {
  // The effective layout includes custom fields. This keeps a value typed into a custom
  // conditional field from lingering in a draft (and consequently Firestore/PDF output)
  // after its trigger becomes false, just like coded conditional fields.
  return buildEffectiveSections(FORM_SECTIONS, config?.fieldLayout).flatMap((section) =>
    section.fields
    .filter((field) =>
      field.path &&
      field.type !== 'notice' &&
      field.type !== 'attachments' &&
      (section.visibleIf || field.visibleIf)
    )
    .map((field) => ({
      path: field.path,
      visibleIf:
        section.visibleIf && field.visibleIf
          ? { all: [section.visibleIf, field.visibleIf] }
          : section.visibleIf || field.visibleIf,
    }))
  );
}

/**
 * path -> effective field, over the same layered layout conditionalFields() uses, so a
 * rule can read a target's TYPE (and therefore its storage shape) instead of being told it.
 */
function effectiveFieldsByPath(config) {
  const map = new Map();
  for (const section of buildEffectiveSections(FORM_SECTIONS, config?.fieldLayout)) {
    for (const field of section.fields) {
      if (field.path) map.set(field.path, field);
    }
  }
  return map;
}

function clearHiddenConditionalValues(matter, config) {
  return conditionalFields(config).reduce(
    (out, field) => (evaluateCondition(field.visibleIf, out) ? out : unsetPath(out, field.path)),
    matter
  );
}

/**
 * Field paths whose conditions do not match the supplied matter. The form uses this
 * alongside the cleaned in-memory draft when saving with Firestore's merge mode:
 * omitting a nested key from a merge payload does not remove an earlier value already
 * stored in Firestore, so these paths must be sent as explicit delete sentinels.
 */
export function hiddenConditionalFieldPaths(matter, config) {
  return conditionalFields(config)
    .filter((field) => !evaluateCondition(field.visibleIf, matter))
    .map((field) => field.path);
}

/**
 * Every draft save must carry a createdAt. The approver "All matters" view
 * (MatterList.jsx) runs `orderBy('createdAt', 'desc')` over the whole matters
 * collection, and Firestore silently drops any document missing the field being
 * ordered on from an orderBy query's results — so a matter without createdAt is
 * invisible there even though it saved fine and the client shows "Draft saved".
 * Set it once on first save and never again, so later autosaves cannot move a
 * matter's position by touching createdAt on every keystroke. Passing the
 * existing matter's current createdAt (if any) makes this idempotent and also
 * self-heals any older draft that predates this fix on its very next autosave.
 */
export function withCreatedAt(matter, timestampFactory) {
  if (matter && matter.createdAt) return matter;
  return { ...matter, createdAt: timestampFactory() };
}

/**
 * Recursively drops any `undefined` values from a plain object/array tree.
 *
 * Firestore's setDoc() throws synchronously ("Unsupported field value: undefined")
 * if ANY key anywhere in the payload holds `undefined` — including deeply nested keys
 * inside arrays of objects (e.g. a repeatable row whose optional sub-field was never
 * touched). A single stray undefined anywhere in the matter tree fails the entire
 * draft save with no partial write, which look to a user exactly like "Save as Draft
 * is broken": clicking it does nothing (or shows a generic "Save failed") no matter
 * how many times they retry, because the same bad value keeps coming along. This is
 * the belt to `save()`'s try/catch suspenders: it's cheap, always safe, and removes
 * an entire class of the most likely explanation for that report even if the
 * offending field can't be pinned down from static review alone. It's applied right
 * before every setDoc, never mutates matter in React state, and Firestore treats an
 * omitted key identically to how the app already treats a never-set field.
 */
export function pruneUndefined(value) {
  if (Array.isArray(value)) return value.map(pruneUndefined);
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    for (const [key, inner] of Object.entries(value)) {
      if (inner === undefined) continue;
      out[key] = pruneUndefined(inner);
    }
    return out;
  }
  return value;
}

/**
 * Practice Group Leader auto-populate (Issue 1).
 *
 * Selecting a Practice Group is documented ("Auto-filled from the selected practice
 * group; override only if needed.") to fill in header.practiceGroupLeaderName from that
 * group's designated leader. This only overwrites the name when the practice group
 * selection itself just changed, so a manual override typed afterwards is left alone
 * until the group is changed again.
 */
function applyPracticeGroupLeaderAutofill(prevMatter, nextMatter, config) {
  const prevGroupId = getPath(prevMatter, 'header.practiceGroupId') || '';
  const nextGroupId = getPath(nextMatter, 'header.practiceGroupId') || '';
  if (prevGroupId === nextGroupId) return nextMatter;

  if (!nextGroupId) {
    return setPath(nextMatter, 'header.practiceGroupLeaderName', '');
  }

  const groups = (config && config.practiceGroups) || [];
  const roster = (config && config.roster) || [];
  const group = groups.find((g) => g.id === nextGroupId);
  const leaderUid = group && group.practiceGroupLeaderUid;
  const leader = leaderUid ? roster.find((u) => u.id === leaderUid) : null;

  return setPath(nextMatter, 'header.practiceGroupLeaderName', leader ? leader.displayName : '');
}

/** True only for a new matter being opened for an existing client. */
function isNewMatterExistingClient(matter) {
  return (
    getPath(matter, 'header.isNewMatter') === true &&
    getPath(matter, 'header.isNewClient') === false
  );
}

function isEmpty(value) {
  return value === undefined || value === null || value === '';
}

/**
 * Existing-client matters ordinarily come from the current client relationship.
 * Set that convenient default only as the form enters this exact state and only
 * when the user has not selected another client source.
 */
function applyExistingClientSourceDefault(prevMatter, nextMatter) {
  if (
    isNewMatterExistingClient(prevMatter) ||
    !isNewMatterExistingClient(nextMatter) ||
    !isEmpty(getPath(nextMatter, 'clientSource.source'))
  ) {
    return nextMatter;
  }
  return setPath(nextMatter, 'clientSource.source', 'current_client');
}

/**
 * New matters for an existing client usually have no prior-representation issue.
 * This is a convenience pre-fill, not an inference: it only writes both "No"
 * answers when both were entirely unanswered as the form enters that state.
 */
function applyExistingClientPriorRepresentationDefaults(prevMatter, nextMatter) {
  if (isNewMatterExistingClient(prevMatter) || !isNewMatterExistingClient(nextMatter)) {
    return nextMatter;
  }

  const previouslyRepresented = getPath(nextMatter, 'priorRepresentation.previouslyRepresented');
  const litigationWithFormerAttorney = getPath(
    nextMatter,
    'priorRepresentation.litigationWithFormerAttorney'
  );
  if (previouslyRepresented !== undefined && previouslyRepresented !== null) return nextMatter;
  if (litigationWithFormerAttorney !== undefined && litigationWithFormerAttorney !== null) {
    return nextMatter;
  }

  let out = setPath(nextMatter, 'priorRepresentation.previouslyRepresented', false);
  out = setPath(out, 'priorRepresentation.litigationWithFormerAttorney', false);
  return out;
}

/**
 * Keep the conflict-search client name in sync with Client Information while it
 * is still blank or demonstrably following the prior client name. A deliberately
 * different conflict-search name is never overwritten.
 */
function applyConflictCheckClientNameMirror(prevMatter, nextMatter) {
  const prevClientName = getPath(prevMatter, 'client.name');
  const nextClientName = getPath(nextMatter, 'client.name') || '';
  const conflictName = getPath(nextMatter, 'conflictCheck.clientFullName');

  if (!isEmpty(conflictName) && conflictName !== prevClientName) return nextMatter;
  if (conflictName === nextClientName) return nextMatter;
  return setPath(nextMatter, 'conflictCheck.clientFullName', nextClientName);
}

/**
 * The matter-owning practice group ordinarily needs Wall Builder access. Any change to
 * this field (initial pick, or later correcting a mistake) adds the new group id to
 * Wall Builder's selection if it isn't already there. This is intentionally
 * one-directional (Header -> Wall Builder only) and does not remove the previous
 * group id from Wall Builder on change — correcting a mistake on Header should never
 * require a second manual fix on Wall Builder, but Wall Builder edits are never
 * overwritten or pruned by this sync.
 */
function applyWallBuilderPracticeGroupDefault(prevMatter, nextMatter) {
  const prevGroupId = getPath(prevMatter, 'header.practiceGroupId') || '';
  const nextGroupId = getPath(nextMatter, 'header.practiceGroupId') || '';
  const practiceGroupIds = getPath(nextMatter, 'wallBuilder.practiceGroupIds');
  const wallBuilder = Array.isArray(practiceGroupIds) ? practiceGroupIds : [];

  if (!nextGroupId || nextGroupId === prevGroupId || wallBuilder.includes(nextGroupId)) {
    return nextMatter;
  }
  return setPath(nextMatter, 'wallBuilder.practiceGroupIds', [...wallBuilder, nextGroupId]);
}

/**
 * Each newly selected additional practice group receives a one-time Wall Builder
 * default. This is intentionally one-directional: removing it from Header or
 * manually removing it from Wall Builder is preserved. Removing it from Header
 * and adding it again is a new add event, so it receives the default again.
 */
export function applyWallBuilderAdditionalPracticeGroupDefaults(prevMatter, nextMatter) {
  const previousIds = getPath(prevMatter, 'header.additionalPracticeGroupIds');
  const nextIds = getPath(nextMatter, 'header.additionalPracticeGroupIds');
  const wallBuilderIds = getPath(nextMatter, 'wallBuilder.practiceGroupIds');
  const previous = Array.isArray(previousIds) ? previousIds : [];
  const next = Array.isArray(nextIds) ? nextIds : [];
  const wallBuilder = Array.isArray(wallBuilderIds) ? wallBuilderIds : [];
  const newlyAdded = next.filter((id) => id && !previous.includes(id) && !wallBuilder.includes(id));

  if (newlyAdded.length === 0) return nextMatter;
  return setPath(nextMatter, 'wallBuilder.practiceGroupIds', [...wallBuilder, ...newlyAdded]);
}

/**
 * Engagement-letter waiver fields must reactively hide AND clear (Issue 2).
 *
 * formSchema.js visibility chain:
 *   waived           visibleIf signedOnFile is_false
 *   waiverReason     visibleIf waived is_true
 *
 * Each field's visibleIf only looks at its OWN condition, not its ancestor's — so when
 * signedOnFile flips back to true, the "waived" question correctly re-hides, but its
 * underlying value is untouched (still true), and waiverReason only checks
 * `waived === true`, which is still satisfied. Result: the reason box stays visible (and
 * its stale value stays in the data) even though the question that revealed it is gone.
 * Fix: whenever signedOnFile is not false, the whole waiver branch is out of play, so
 * reset `waived` (and, belt-and-suspenders, the leaf field) back to its default.
 *
 * Note (v1.3.48): the "Initials of Managing Partner waiving the engagement letter" field
 * was removed — it asked Intake to pre-record a sign-off that the real
 * `engagement_letter_waiver` approval step (Managing Partner resolver, see
 * functions/src/approval/flowDefaults.js) already performs for real, with its own audit
 * trail. That approval step is the source of truth for who approved the waiver.
 */
function applyEngagementLetterWaiverReset(prevMatter, nextMatter) {
  const signedOnFile = getPath(nextMatter, 'header.engagementLetter.signedOnFile');
  let out = nextMatter;

  if (signedOnFile !== false) {
    if (getPath(out, 'header.engagementLetter.waived')) {
      out = setPath(out, 'header.engagementLetter.waived', false);
    }
  }
  if (getPath(out, 'header.engagementLetter.waived') !== true) {
    if (getPath(out, 'header.engagementLetter.waiverReason')) {
      out = setPath(out, 'header.engagementLetter.waiverReason', '');
    }
  }

  return out;
}

/**
 * Matter Type is a single-select control (Issue 4) backed by two legacy boolean paths
 * (matterType.isLitigation / matterType.isNonLitigation) that the rest of the app
 * (approval-flow conditions, server-side chain builder) already reads independently.
 * Keeping both booleans in sync here means nothing downstream needs to change shape.
 *
 * matterType.selection ('litigation' | 'non_litigation' | '') is written directly by
 * MatterTypeSelect's own onChange (Field.jsx), but this derivation is the safety net for
 * any other path that flips the booleans programmatically (e.g. a future admin tool or
 * bulk edit) without going through that control — selection is kept in lockstep here too
 * so the rules engine / PDF renderer, which key off selection, never fall out of sync
 * with the booleans.
 */
function applyMatterTypeExclusivity(prevMatter, nextMatter) {
  const prevLit = getPath(prevMatter, 'matterType.isLitigation') === true;
  const prevNonLit = getPath(prevMatter, 'matterType.isNonLitigation') === true;
  const nextLit = getPath(nextMatter, 'matterType.isLitigation') === true;
  const nextNonLit = getPath(nextMatter, 'matterType.isNonLitigation') === true;

  let out = nextMatter;
  if (nextLit !== prevLit || nextNonLit !== prevNonLit) {
    if (nextLit && !prevLit) {
      // Litigation was just turned on: Non-Litigation must turn off.
      out = setPath(out, 'matterType.isNonLitigation', false);
    } else if (nextNonLit && !prevNonLit) {
      // Non-Litigation was just turned on: Litigation must turn off.
      out = setPath(out, 'matterType.isLitigation', false);
    }
  }

  const finalLit = getPath(out, 'matterType.isLitigation') === true;
  const finalNonLit = getPath(out, 'matterType.isNonLitigation') === true;
  const expectedSelection = finalLit ? 'litigation' : finalNonLit ? 'non_litigation' : '';
  if (getPath(out, 'matterType.selection') !== expectedSelection) {
    out = setPath(out, 'matterType.selection', expectedSelection);
  }

  return out;
}

/**
 * A single working attorney implicitly gets 100% of the origination credit (Issue 5
 * default-percentage decision) — there is nothing to split, so we do not make the user
 * type "100" by hand. Only applies the very first time a lone row appears with no
 * percent set yet; never overwrites a value the user already entered.
 */
function applySoleOriginatorDefault(prevMatter, nextMatter) {
  const rows = getPath(nextMatter, 'origination.workingAttorneys');
  if (!Array.isArray(rows) || rows.length !== 1) return nextMatter;
  const row = rows[0];
  if (row && row.uid && (row.percent === undefined || row.percent === null || row.percent === '')) {
    return setPath(nextMatter, 'origination.workingAttorneys', [{ ...row, percent: 100 }]);
  }
  return nextMatter;
}

/**
 * Responsible Partner mirror (data-integrity fix).
 *
 * `header.responsiblePartnerUid` (Header & Billing) and `origination.responsiblePartnerUid`
 * (Origination of File / Working Attorneys) used to be two independently-editable fields
 * for what is always the same real-world person, which let them silently diverge. The
 * header field is now the single source of truth; this derivation keeps the origination
 * path as a read-only mirror of it, unconditionally, on every derive — unlike
 * applyPracticeGroupLeaderAutofill above, there is no legitimate independent value for the
 * origination copy to protect, so this does not gate on "did the header field just
 * change". The origination path is intentionally not removed: functions/src/documents/
 * intakeFieldManifest.js and matterRecordPdf.js still read origination.responsiblePartnerUid
 * for the PDF/manifest, and mirroring it here keeps that data shape and those readers
 * working with no server-side changes needed.
 */
function applyResponsiblePartnerMirror(nextMatter) {
  const headerUid = getPath(nextMatter, 'header.responsiblePartnerUid') || '';
  if (getPath(nextMatter, 'origination.responsiblePartnerUid') === headerUid) return nextMatter;
  return setPath(nextMatter, 'origination.responsiblePartnerUid', headerUid);
}

/**
 * Admin-authored "set a default once a condition becomes true" rule
 * (settings/fieldDerivations, rule.kind === 'setDefault'). Generalizes
 * applyExistingClientSourceDefault above: fires only on the prev->next transition edge of
 * rule.when (it was not already true, and is true now), and only if the target field is
 * still empty — the same "enters this exact state, never clobbers" behavior, just
 * parameterized by an admin-supplied condition/target/value instead of a hardcoded one.
 */
function applySetDefaultRule(rule, prevMatter, nextMatter) {
  const isTrue = evaluateCondition(rule.when, nextMatter);
  /**
   * lockTarget turns "suggest this once" into "this IS the answer". Both the edge check and the
   * still-empty check are skipped, because both exist to protect a value the user chose — and a
   * locked target is greyed out, so there is no user choice to protect. What is left to correct
   * is a value that drifted in from somewhere else: another rule, a client feed, or a draft saved
   * before this rule existed. Those are exactly the cases where the form would otherwise display
   * a value its own rule contradicts.
   */
  if (rule.lockTarget) {
    if (!isTrue) return nextMatter;
    if (getPath(nextMatter, rule.targetPath) === rule.value) return nextMatter;
    return setPath(nextMatter, rule.targetPath, rule.value);
  }
  const wasTrue = evaluateCondition(rule.when, prevMatter);
  if (wasTrue || !isTrue) return nextMatter;
  if (!isEmpty(getPath(nextMatter, rule.targetPath))) return nextMatter;
  return setPath(nextMatter, rule.targetPath, rule.value);
}

/**
 * Admin-authored "mirror source into target until the user diverges" rule
 * (rule.kind === 'mirror'). Generalizes applyConflictCheckClientNameMirror above: keeps
 * target equal to source's value while target is still blank or still demonstrably
 * following the prior source value; a deliberately different target is never overwritten,
 * but mirroring resumes if target is blanked back to empty.
 */
function applyMirrorRule(rule, prevMatter, nextMatter) {
  const prevSourceValue = getPath(prevMatter, rule.sourcePath);
  const nextSourceValue = getPath(nextMatter, rule.sourcePath) || '';
  const targetValue = getPath(nextMatter, rule.targetPath);

  if (targetValue === nextSourceValue) return nextMatter;
  // "Until the user diverges" is the whole behaviour of an unlocked mirror. Locking it removes
  // that escape hatch: the target simply equals the source, always.
  if (!rule.lockTarget && !isEmpty(targetValue) && targetValue !== prevSourceValue) return nextMatter;
  return setPath(nextMatter, rule.targetPath, nextSourceValue);
}

/**
 * The three directory lists a 'lookup' rule can index into — the same lists
 * useFormConfig() already loads into `config` for applyPracticeGroupLeaderAutofill above.
 * settings/areasOfLaw's own document shape nests its list under `.items`; roster and
 * practiceGroups are already flat arrays.
 */
function lookupDirectory(config, name) {
  if (name === 'roster') return Array.isArray(config?.roster) ? config.roster : [];
  if (name === 'practiceGroups') return Array.isArray(config?.practiceGroups) ? config.practiceGroups : [];
  if (name === 'areasOfLaw') return Array.isArray(config?.areasOfLaw?.items) ? config.areasOfLaw.items : [];
  // `feed:<id>` has no list here on purpose: the form never loads a directory of ten thousand
  // records, so a feed lookup is answered from the copy the picker stored on the matter (see
  // selectionRecord). Pointed at a field that is not a picker, it finds nothing and writes
  // nothing — which is the right answer rather than a guess.
  return [];
}

/**
 * The record a directory SELECTION is already carrying, or null if this is not one.
 *
 * `roster`, `practiceGroups` and `areasOfLaw` are small enough for the form to hold all of them,
 * so a lookup into those finds the record in a list. A client directory is not: there are 10,595
 * of them and the form never loads the collection. What it has instead is better — the picker
 * stores a copy of the chosen row ON THE MATTER, so a lookup reads that.
 *
 * Reading the stored copy rather than re-querying is deliberate, and matches what the feed's own
 * column mapping does. Filling the form in must not depend on the directory still being
 * reachable, and a matter should keep the values it was actually filled in with — a client whose
 * address changes in next month's export must not silently rewrite a matter that was already
 * filled in and approved on the old one.
 *
 * The record's own number and name are exposed alongside its columns, and last, so `clientNumber`
 * and `clientName` always mean the record's identity even if a column happens to share the name.
 */
function selectionRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (typeof value.clientId !== 'string' || !value.clientId) return null;
  const data = value.data && typeof value.data === 'object' && !Array.isArray(value.data) ? value.data : {};
  return {
    ...data,
    id: value.clientId,
    clientNumber: value.clientNumber || '',
    clientName: value.clientName || '',
  };
}

/**
 * Admin-authored "look up a related record and copy one of its fields" rule
 * (rule.kind === 'lookup'). Generalizes applyPracticeGroupLeaderAutofill above:
 * recomputes only when rule.sourcePath's value just changed, always overwrites the
 * target with the matched record's attribute (unlike setDefault/mirror, there is no
 * empty-gate — this is a computed display field, not an answer to protect) and clears
 * the target back to '' when the source is cleared. A manual override on the target
 * survives until the source changes again, same as the precedent's "override only if
 * needed" contract. `rule.then` optionally chains a second lookup off the first
 * attribute's value (e.g. a practice group's leader-uid into the roster, reproducing the
 * exact group -> leader-name autofill in one rule).
 */
function applyLookupRule(rule, prevMatter, nextMatter, config, fields) {
  const prevRaw = getPath(prevMatter, rule.sourcePath);
  const nextRaw = getPath(nextMatter, rule.sourcePath);
  // A picker stores an object; every other source stores the record's id as a string. Both
  // reduce to an id, which is what "has the source changed?" is really asking.
  const selection = selectionRecord(nextRaw);
  const prevSelection = selectionRecord(prevRaw);
  const prevId = prevSelection ? prevSelection.id : prevRaw || '';
  const nextId = selection ? selection.id : nextRaw || '';

  /**
   * Unlocked, this recomputes only when the source just changed — which is what lets a manual
   * override typed afterwards survive. Locked, there is no manual override to survive (the target
   * is greyed out), so it recomputes every pass and any value that drifted is corrected.
   */
  if (!rule.lockTarget && prevId === nextId) return nextMatter;

  const write = (value) =>
    getPath(nextMatter, rule.targetPath) === value
      ? nextMatter
      : setPath(nextMatter, rule.targetPath, value);

  if (!nextId) return write('');

  const record = selection || lookupDirectory(config, rule.lookupIn).find((r) => r.id === nextId);
  let value = record ? record[rule.attribute] : undefined;
  if (rule.then && value) {
    const nextRecord = lookupDirectory(config, rule.then.lookupIn).find((r) => r.id === value);
    value = nextRecord ? nextRecord[rule.then.attribute] : undefined;
  }
  // Through the same coercion the feed's own column mapping uses: a directory stores text, and a
  // Yes/No holds a boolean while a dropdown holds one of its own option values. A client's
  // "Retainer" column reading "NO" must arrive as a No, not as a Yes/No showing nothing selected.
  const coerced = feedValueForField(value === undefined || value === null ? '' : value, fields?.get(rule.targetPath));
  return write(coerced === undefined || coerced === null ? '' : coerced);
}

/**
 * Admin-authored "copy people into another field, add-only" rule
 * (rule.kind === 'appendPeople'). Generalizes
 * applyWallBuilderAdditionalPracticeGroupDefaults above: each person newly added to
 * `sourcePath` is appended to `targetPath` once, and removing them from the source NEVER
 * removes them from the target.
 *
 * That asymmetry is the whole point for Working Attorneys -> Origination Split: the two
 * lists agree ~95% of the time, but an attorney can hold origination credit without
 * working the matter (Finance tracks credit and staffing separately), so the target must be
 * free to keep people the source has lost. Removing someone from the source and adding them
 * back is a fresh add event, so they are appended again.
 *
 * The target's SHAPE comes from the effective schema, not from configuration: a
 * peopleMultiSelect/personSearch target stores bare uids, an originationSplit target stores
 * `{ uid, percent }` rows. Reading it from the schema means the rule cannot be misconfigured
 * into writing the wrong shape, and an unknown/absent target field is skipped rather than
 * guessed at.
 *
 * A newly appended origination row gets 100% when it is the only row and 0% otherwise.
 * 0 is deliberate: origination percentages feed partner advancement, so inventing a split
 * for a second attorney would be a silent governance guess. 0 forces a deliberate number,
 * and the 100%-total check in fieldRules.js blocks submission until someone sets it.
 */
function applyAppendPeopleRule(rule, prevMatter, nextMatter, config) {
  const fieldsByPath = effectiveFieldsByPath(config);
  const target = fieldsByPath.get(rule.targetPath);
  if (!target) return nextMatter;

  const uidsOf = (value) =>
    (Array.isArray(value) ? value : [])
      .map((entry) => (entry && typeof entry === 'object' ? entry.uid : entry))
      .filter(Boolean);

  const previous = uidsOf(getPath(prevMatter, rule.sourcePath));
  const next = uidsOf(getPath(nextMatter, rule.sourcePath));
  const existingRaw = Array.isArray(getPath(nextMatter, rule.targetPath))
    ? getPath(nextMatter, rule.targetPath)
    : [];
  const existing = new Set(uidsOf(existingRaw));
  const newlyAdded = next.filter((uid) => !previous.includes(uid) && !existing.has(uid));
  if (newlyAdded.length === 0) return nextMatter;

  if (target.type === 'originationSplit') {
    const rows = [...existingRaw];
    for (const uid of newlyAdded) {
      const onlyRow = rows.filter((r) => r && r.uid).length === 0;
      rows.push({ uid, percent: onlyRow ? 100 : 0 });
    }
    return setPath(nextMatter, rule.targetPath, rows);
  }
  if (target.type === 'peopleMultiSelect' || target.type === 'personSearch') {
    return setPath(nextMatter, rule.targetPath, [...uidsOf(existingRaw), ...newlyAdded]);
  }
  return nextMatter; // any other target type has no people-list shape to append to
}

/**
 * Fills in the form from a chosen client, using the column mapping an administrator set up
 * in Admin → Directories → Feeds (`settings/feeds.feeds.clients.columns[].target`).
 *
 * Every `clientSearch` field in the effective schema is watched. When its selection CHANGES
 * — a different client picked, or the selection cleared — each mapped column is written to
 * the form field it names; clearing the client clears them. Between selections nothing is
 * touched, so a person who corrects an imported address keeps their correction until they
 * pick a different client, exactly like the practice-group-leader autofill it is modelled on.
 *
 * The values come from the copy of the imported row stored on the matter by the picker, not
 * from a lookup: filling in the form must not depend on the directory still being reachable,
 * and a matter should keep the values it was actually filled in with.
 *
 * A mapped target that no longer exists in the schema is a harmless no-op — setPath just
 * writes a value nothing renders — which is why a stale mapping is not worth validating
 * against the live form.
 */

/** YES/NO as the exports actually spell it, on both sides. */
const FEED_TRUE = ['yes', 'y', 'true', 't', '1'];
const FEED_FALSE = ['no', 'n', 'false', 'f', '0'];

/**
 * A feed's value, in the shape the control it is filling can actually hold.
 *
 * An export stores text. A Yes/No holds `true` or `false` — `value === true` is what selects a
 * pill — and a dropdown holds one of its own option values. Writing the string "YES" into a
 * Yes/No therefore produces a field that shows NOTHING selected while holding a value, and
 * because it is not empty it satisfies a `required` check: the matter is submitted with a
 * question that looks unanswered on screen and answered to the validator. That is live today
 * on the production client feed, where `client_Retainer` ("YES"/"NO") fills the "Retainer?"
 * Yes/No.
 *
 * A value that cannot be recognised becomes BLANK rather than being written through. A blank
 * required field is caught at submission and asked about; a "maybe" sitting invisibly in a
 * Yes/No is not caught by anything.
 *
 * Every other type is passed through untouched. Only the two shapes with evidence behind them
 * are coerced — a text box holds whatever text the export had, and inventing conversions for
 * dates or money without a real feed to check them against would be guessing.
 */
function feedValueForField(value, field) {
  // No such field in the schema — a stale mapping. setPath writes a value nothing renders,
  // which is the existing harmless no-op; there is nothing to coerce it towards.
  if (!field) return value;
  const text = String(value === undefined || value === null ? '' : value).trim();
  if (!text) return '';

  if (field.type === 'yesno') {
    if (FEED_TRUE.includes(text.toLowerCase())) return true;
    if (FEED_FALSE.includes(text.toLowerCase())) return false;
    return '';
  }
  if (field.type === 'select' || field.type === 'radio') {
    const options = Array.isArray(field.options) ? field.options : [];
    // Matched on the option's value OR its label, and case-insensitively: an export writes
    // "PRIVATE" where the option is `private`, and a firm's own list is as likely to be keyed by
    // the label a person reads as by a slug.
    const match = options.find((option) => {
      const optionValue = String((option && option.value) ?? option ?? '').trim();
      const optionLabel = String((option && option.label) ?? '').trim();
      return (
        optionValue.toLowerCase() === text.toLowerCase() || optionLabel.toLowerCase() === text.toLowerCase()
      );
    });
    if (!match) return '';
    return String((match && match.value) ?? match ?? '');
  }
  return value;
}
function applyClientFeedMapping(prevMatter, nextMatter, config) {
  let out = nextMatter;
  const fields = effectiveFieldsByPath(config);
  for (const [path, field] of fields) {
    if (field.type !== 'clientSearch') continue;
    // Each field names the directory it picks from, so two fields can fill in from two
    // different feeds on the same form.
    const feedId = field.feedId || 'clients';
    const columns = config?.feeds?.feeds?.[feedId]?.columns;
    if (!Array.isArray(columns) || !columns.length) continue;
    const targets = columns.filter(
      (column) =>
        column &&
        column.key &&
        column.role !== 'ignore' &&
        typeof column.target === 'string' &&
        column.target.trim()
    );
    if (!targets.length) continue;
    const before = getPath(prevMatter, path);
    const after = getPath(out, path);
    const beforeId = (before && before.clientId) || '';
    const afterId = (after && after.clientId) || '';
    if (beforeId === afterId) continue;

    const data = (after && after.data) || {};
    for (const column of targets) {
      const target = column.target.trim();
      // The client's own number and name are useful targets too, not just `data` columns:
      // a firm will usually want the name copied into the matter's own client-name field.
      const value =
        column.role === 'number'
          ? (after && after.clientNumber) || ''
          : column.role === 'name'
            ? (after && after.clientName) || ''
            : data[column.key] || '';
      out = setPath(out, target, afterId ? feedValueForField(value, fields.get(target)) : '');
    }
  }
  return out;
}

/**
 * Kinds where "don't allow it to be changed" means something.
 *
 * `appendPeople` is excluded on purpose: it exists to ADD to whoever the user picked, so greying
 * the field out would remove the only thing it is for.
 */
const LOCKABLE_DERIVATION_KINDS = ['setDefault', 'mirror', 'lookup'];

export function isLockableDerivationKind(kind) {
  return LOCKABLE_DERIVATION_KINDS.includes(kind);
}

/**
 * Whether an auto-fill rule is currently holding a field's value against being edited.
 *
 * This is what greys the control out, and it is asked of the SAME rule objects that write the
 * value — so a field can never be greyed out by a rule that is not actually setting it, or be
 * editable while a rule silently overwrites what is typed.
 *
 * A 'setDefault' rule holds the field only while its condition is true; the field goes back to
 * being editable when it stops matching, keeping whatever value it had. 'mirror' and 'lookup'
 * are continuous by nature — they have no `when` — so they hold it whenever they are enabled.
 */
export function isPathLockedByDerivation(path, matter, config) {
  if (!path) return false;
  const rules = config?.fieldDerivations?.rules || [];
  return rules.some((rule) => {
    if (!rule || rule.enabled === false || !rule.lockTarget) return false;
    if (rule.targetPath !== path) return false;
    if (!isLockableDerivationKind(rule.kind)) return false;
    return rule.kind === 'setDefault' ? evaluateCondition(rule.when, matter) : true;
  });
}

/** Runs every enabled admin-authored rule from settings/fieldDerivations, in array order. */
function applyConfiguredDerivations(prevMatter, nextMatter, config) {
  const rules = config?.fieldDerivations?.rules || [];
  if (!rules.length) return nextMatter;
  // Built once for the whole pass, not per rule: merging the layout is the expensive part of
  // this and a firm with thirty rules would otherwise do it thirty times per keystroke.
  const fields = effectiveFieldsByPath(config);
  return rules.reduce((out, rule) => {
    if (!rule || rule.enabled === false) return out;
    if (rule.kind === 'setDefault') return applySetDefaultRule(rule, prevMatter, out);
    if (rule.kind === 'mirror') return applyMirrorRule(rule, prevMatter, out);
    if (rule.kind === 'lookup') return applyLookupRule(rule, prevMatter, out, config, fields);
    if (rule.kind === 'appendPeople') return applyAppendPeopleRule(rule, prevMatter, out, config);
    return out;
  }, nextMatter);
}

/** Runs every derivation, in order, after any form edit. */
function defaultSpecs(config) {
  const rules = config?.fieldRules?.rules || {};
  const specs = [];
  for (const [path, field] of effectiveFieldsByPath(config)) {
    // A layout-level starting value beats settings/fieldRules, the same way requirementOverride
    // beats the rules document's requirement — more specific, more recently edited, and it has to
    // win without erasing what it overrides.
    //
    // The condition comes from WHICHEVER SOURCE supplied the value, never mixed: taking the value
    // from the layout and the condition from the rules document would produce a rule neither of
    // them says, and one nobody could read off either screen.
    const fromLayout = isUsableDefault(field.defaultValue);
    const value = fromLayout ? field.defaultValue : rules[path]?.defaultValue;
    if (!isUsableDefault(value)) continue;
    specs.push({
      path,
      field,
      value,
      when: fromLayout ? field.defaultIf : rules[path]?.defaultIf,
    });
  }
  return specs;
}

/**
 * Whether a starting value applies to a matter right now: the field is on screen AND its own
 * condition matches.
 *
 * `when` being undefined reads as "no extra condition", which is exactly what evaluateCondition
 * returns true for, and is the correct reading here. Note this is the OPPOSITE of `disabledIf`,
 * where that same default would mean "always disabled" — see the guard in Field.jsx.
 */
function defaultApplies(spec, matter) {
  return evaluateCondition(spec.field.visibleIf, matter) && evaluateCondition(spec.when, matter);
}

/**
 * Per-field starting values — from settings/fieldRules (`defaultValue`) or a Field Builder
 * override — each optionally gated on its own condition (`defaultIf`).
 *
 * Applied on the EDGE where a field starts applying: it became visible, or its condition became
 * true. And only into a blank target. Both halves matter:
 *
 * - Edge-triggered, so clearing a defaulted box leaves it cleared. An unconditional "if blank,
 *   fill it" would refill the field on the next keystroke anywhere on the form, which is the
 *   difference between a default and a value the user is not allowed to delete.
 * - On the edge rather than only at creation, because `clearHiddenConditionalValues` runs last on
 *   every pass and strips the value of any field whose condition is false. A default written into
 *   a conditionally-revealed field at creation time would be erased before anyone saw the field.
 *
 * `applyInitialFieldDefaults` below covers the other case — a field that already applies on a
 * blank form, which has no edge to catch.
 */
function applyFieldDefaults(prevMatter, nextMatter, config) {
  const specs = defaultSpecs(config);
  if (specs.length === 0) return nextMatter;

  return specs.reduce((out, spec) => {
    if (!defaultApplies(spec, out)) return out;
    if (defaultApplies(spec, prevMatter)) return out;
    if (!isBlank(getPath(out, spec.path))) return out;
    return setPath(out, spec.path, spec.value);
  }, nextMatter);
}

/**
 * The same starting values, for a matter being created rather than edited.
 *
 * A field that already applies on a blank form has no edge for `applyFieldDefaults` to catch, so
 * this runs once against the empty matter. Deliberately NOT part of deriveMatter's per-keystroke
 * pipeline.
 */
export function applyInitialFieldDefaults(matter, config) {
  const specs = defaultSpecs(config);
  if (specs.length === 0) return matter;

  return specs.reduce((out, spec) => {
    if (!defaultApplies(spec, out)) return out;
    if (!isBlank(getPath(out, spec.path))) return out;
    return setPath(out, spec.path, spec.value);
  }, matter);
}

export function deriveMatter(prevMatter, nextMatter, config) {
  let out = nextMatter;
  out = applyPracticeGroupLeaderAutofill(prevMatter, out, config);
  out = applyExistingClientSourceDefault(prevMatter, out);
  out = applyExistingClientPriorRepresentationDefaults(prevMatter, out);
  out = applyConflictCheckClientNameMirror(prevMatter, out);
  out = applyWallBuilderPracticeGroupDefault(prevMatter, out);
  out = applyWallBuilderAdditionalPracticeGroupDefaults(prevMatter, out);
  out = applyEngagementLetterWaiverReset(prevMatter, out);
  out = applyMatterTypeExclusivity(prevMatter, out);
  out = applySoleOriginatorDefault(prevMatter, out);
  out = applyResponsiblePartnerMirror(out);
  // Runs before the admin-authored rules, so a rule can react to what picking a client just
  // filled in rather than to the state before it.
  out = applyClientFeedMapping(prevMatter, out, config);
  out = applyConfiguredDerivations(prevMatter, out, config);
  // After the derivations, so an auto-fill rule that just wrote a value wins over a default,
  // and before the hidden-value clear, which still has the last word on invisible fields.
  out = applyFieldDefaults(prevMatter, out, config);
  out = clearHiddenConditionalValues(out, config);
  return out;
}
